// Canonical user-facing names for Tweedia's four edit operations (#379).
// Single source of truth: components render these strings verbatim — never
// re-typed, never re-cased/transformed at the call site.
// Rules:
//  - Capability noun is "stillness"; the feature is "automatic stillness
//    detection"; the verb form is "trim the stillness". No dead space/air/frames.
//  - One shared set of display names across buyer and hobbyist pages. The
//    REGISTER SPLIT stays in the surrounding prose: buyer pages (LandingPage,
//    TimelapseCalculatorPage) say jobsite/progress/documentation/stakeholders;
//    hobbyist pages (LongTermTimelapseGuidePage) say holy grail/keyframes/XMP/
//    RAW/interval/deflicker. Never mix registers on one page.
//  - displayName vs seoSlug may DIFFER, and where they do it is deliberate:
//    deflicker displays as "Fix flicker" but keeps the slug "deflicker" because
//    that is the word hobbyists search. seoSlug is null when the natural slug
//    of displayName is correct.
//  - `deflicker`/`removeWatermark`/`transitionFrames` as settings or API fields
//    are wire format, not copy — do not rename them to match displayName.
// Consumed by the operation registry (#400) and the Edits cards (#401).

export const CAPABILITY = {
  noun: 'stillness',
  feature: 'automatic stillness detection',
  verb: 'trim the stillness',
};

// The word for ONE of the source video files a multi-video project holds.
//
// It is "clip", everywhere: the footage list header, the combine CTA, the
// dropzones. Three reasons, in order:
//  1. A project has exactly one "video" — the thing in its source-video slot,
//     the thing an edit renders from, the thing a combine PRODUCES. Calling the
//     inputs videos too makes "4 videos → combine → video" unreadable.
//  2. The server already says clip: `delete_footage_clip`, `CLIP_NOT_FOUND`,
//     `sourceCount`. Support threads and the UI now use one noun.
//  3. It is what the camera produces — a long shoot split across many files —
//     and the surrounding copy ("Pick every clip from the shoot") already
//     said so before this pass.
// Same rule as the operation names above: components render these verbatim and
// never re-type the word, so the three surfaces cannot drift apart again.
export const FOOTAGE = {
  unit: 'clip',
  plural: 'clips',
};

/** "1 clip" / "4 clips" — the count chip on the footage list and the CTA. */
export function footageCountLabel(count) {
  const n = Number(count) || 0;
  return `${n.toLocaleString()} ${n === 1 ? FOOTAGE.unit : FOOTAGE.plural}`;
}

export const OPERATIONS = {
  trim_stillness: {
    key: 'trim_stillness',
    displayName: 'Trim stillness',
    controlLabel: 'Trim stillness',
    seoSlug: null,
  },
  // MotionPace (#528's `variable_speed`). It has no `copyKey` in the operation
  // registry ON PURPOSE — a copyKey is what gives an operation its own card, and
  // MotionPace does not get one: it is a MODE of the Motion section below,
  // mutually exclusive with MotionTrim. The name still has to live here, because
  // this module is the single naming source and the segmented toggle renders it.
  variable_speed: {
    key: 'variable_speed',
    displayName: 'MotionPace',
    controlLabel: 'MotionPace',
    seoSlug: null,
  },
  // The section that HOLDS the two of them. Also not a registry operation —
  // there is no "motion" step in the ffmpeg chain — but it is the name on the
  // card, so it belongs to the same naming rule as the operations inside it.
  motion: {
    key: 'motion',
    displayName: 'Motion',
    controlLabel: 'Motion',
    seoSlug: null,
  },
  deflicker: {
    key: 'deflicker',
    displayName: 'Fix flicker',
    controlLabel: 'Fix flicker',
    seoSlug: 'deflicker',
  },
  transitions: {
    key: 'transitions',
    displayName: 'Transitions',
    controlLabel: 'Transition between clips',
    seoSlug: null,
  },
  watermark: {
    key: 'watermark',
    displayName: 'Watermark',
    controlLabel: 'Remove watermark',
    seoSlug: null,
  },
  // NOT A CARD ANY MORE, AND NOT A PIPELINE OPERATION EITHER (no entry in
  // operations.json). This is a PLACE the crossfade `transitions` already
  // renders lands — the joins `video.captureGapFrames` names, where shooting
  // stopped for 4+ hours — so it is a row in that card's "Fade at" checklist.
  // The key survives because the draft key, the persisted key and the
  // `operation_toggled` analytics id are all still `capture_gap_transitions`.
  //
  // Naming rule: never "day" transitions/boundaries, since a capture gap can
  // span any stretch of paused shooting, not just a night. "Camera pauses" is
  // the row's own words — plainer than "capture gap", and it is what the user
  // did rather than what the analyzer found.
  capture_gap_transitions: {
    key: 'capture_gap_transitions',
    displayName: 'Camera pauses',
    controlLabel: 'Fade at camera pauses',
    seoSlug: null,
  },
  // The sibling declaration, and for the same reason a checklist row rather
  // than a card: a PLACE the crossfade lands — the joins
  // `video.concatJoinFrames` names, every boundary where one uploaded clip ends
  // and the next begins in a combined video.
  //
  // It exists because the capture-gap rule can only fade a join it can PROVE
  // was a long stop in shooting, and that proof needs trustworthy capture times
  // on both clips. Re-exported footage often carries none, so a shoot that is
  // obviously one clip per session promotes zero gaps and never fades. This is
  // the user declaring what the timestamps could not.
  //
  // Naming rule (same as above): never "day" — a combined video's clips can be
  // one per session, per card or per site visit, not only one per day.
  clip_join_transitions: {
    key: 'clip_join_transitions',
    displayName: 'Clip joins',
    controlLabel: 'Fade at clip joins',
    seoSlug: null,
  },
};

// The two ways to handle a still stretch, KEYED BY THE MODE ID lib/editDraft.js
// uses (MOTION_MODE_TRIM / MOTION_MODE_PACE).
//
// WHY THE TRIM MODE IS CALLED "MotionTrim" HERE AND "Trim stillness" EVERYWHERE
// ELSE. The two are a PAIR now, and a segmented toggle reading "Trim stillness |
// MotionPace" makes them look like different kinds of thing — one a setting, one
// a feature — when they are two answers to the same question. Inside this
// section they are branded as a pair, which is what the approved comps show
// (tweedia-comps/16-motion-trim.html, 17-motion-pace.html). Any surface that
// pair-presents the two — this toggle, the /features and /motiontrim marketing
// pages, which import `MOTION_MODES.trim.name` rather than retype it, and the
// render receipt's Motion row — uses the "MotionTrim" pair brand. The receipt
// is on this side because its one row prints a THREE-WAY choice, MotionTrim /
// MotionPace / Off, so the pair is on screen together exactly as in the toggle;
// naming only one half "Trim stillness" there would reintroduce the two-names-
// for-one-thing problem this rule exists to stop. Standalone mentions — the
// guides, the door pages — keep the operation's own
// `OPERATIONS.trim_stillness.displayName`, which stays the canonical name and
// is what the "stillness" capability noun above is built on.
//
// Pair-brand surfaces read `.name`, never `.badge`: `badge` is the collapsed
// card's state word and is free to shorten independently.
//
// The pace name is TAKEN from the operation rather than re-typed, so the two
// cannot drift.
//
// `description` is the one line under each mode name in the toggle. Each says
// what happens to the still footage, because that is the entire difference: one
// deletes it, the other plays it fast. Neither may claim the other is worse.
export const MOTION_MODES = {
  trim: {
    id: 'trim',
    name: 'MotionTrim',
    description: 'Cut the still stretches',
    // The state badge on the collapsed card, so the section says which mode is
    // armed without being expanded.
    badge: 'MotionTrim',
  },
  pace: {
    id: 'pace',
    name: OPERATIONS.variable_speed.displayName,
    description: 'Speed through the quiet, slow for the action',
    badge: OPERATIONS.variable_speed.displayName,
  },
};

// The Motion section's own strings: the sentence under the title, the label on
// the segmented toggle, and what the switch means when it is off.
export const MOTION_SECTION = {
  displayName: OPERATIONS.motion.displayName,
  subtitle: 'One motion pass over the footage, two ways to use it.',
  modeGroupLabel: 'How to handle still stretches',
  offReason: 'Every frame plays at the same speed — nothing is cut and nothing is sped up.',
};

// MotionPace's two controls. Nothing else is exposed: the algorithm's other
// numbers (the slew limit, the normalization percentile, the speed floor) are
// tuning that was signed off on against real renders, and `smoothness` already
// sets the ramp proportionally — see the module docstring in
// packages/functions/shared/motion_pace.py.
//
// Each caption names what the control COSTS, not just what it does, the same
// rule the Fix flicker strength tiles follow: a control described only by its
// upside reads as a free upgrade and gives the user no reason to move it.
export const MOTION_PACE_CONTROLS = {
  maxSpeed: {
    key: 'maxSpeed',
    label: 'Max speed',
    hint: 'The fastest the quiet stretches will play. Busy stretches always play at 1x, so this only changes how quickly the footage gets you to them.',
    caption: 'The ceiling for the quietest stretches. Busy stretches always play at 1x.',
    // Named at the ends of the track rather than as bare numbers, because the
    // top of the range is a real tradeoff: past about 6x the motion measurement
    // saturates and the pacing stops discriminating at all. The track now runs
    // to 15x (a 2026-08-31 product call — see the bounds section of
    // packages/functions/shared/motion_pace.py), so most of its upper half is
    // on the wrong side of that finding and "Fastest" is a literal claim about
    // speed, not a claim that the pacing gets better.
    minLabel: 'Gentle',
    maxLabel: 'Fastest',
  },
  smoothness: {
    key: 'smoothness',
    label: 'Smoothness',
    // NOT "read like a camera move": the camera does not move in a fixed-position
    // timelapse, so that phrase named something the viewer never sees. What a
    // long ramp actually buys is that the speed changes stay gradual.
    hint: 'How long the speed takes to change. Short ramps react to every burst; long ramps hold one speed through a whole stretch so changes stay gradual.',
    caption: 'How quickly the speed is allowed to change. Snappier reacts to shorter bursts.',
    minLabel: 'Snappy',
    maxLabel: 'Gentle ramp',
  },
  // Not a slider and not a tradeoff, so it carries no caption or endpoints. The
  // hint says the one thing a user cannot see from the switch: the dial is
  // BURNED INTO the file, not a preview decoration, so turning it on later
  // costs a re-render. Named "Speed dial" because that is what the design was
  // signed off as.
  showSpeedDial: {
    key: 'showSpeedDial',
    label: 'Speed dial',
    hint: 'Burns a small dial into the corner of the video whose sweep spins with the playback speed. Part of the render, so changing it means rendering again.',
    caption: 'Shows the viewer how fast each stretch is playing. Burned into the video.',
  },
};

// The pace chart's own labels. Kept beside the operation names for the same
// reason those are here: the chart names the same two quantities the section
// header does, and a legend that drifts from the control above it is worse than
// no legend.
export const MOTION_PACE_CHART = {
  title: 'Speed follows motion',
  motionLabel: 'Motion',
  speedLabel: 'Playback speed',
  ceilingLabel: 'Max speed',
  // Shown while the speed curve is being recomputed server-side, which is the
  // only way a new curve can arrive (#528: the curve is computed once, on the
  // server, and every consumer reads that one record).
  recalculating: 'Recalculating…',
  // Why the "Edited Duration" stat shows `--` rather than a time while that
  // recompute is outstanding. Max speed scales output length directly, so a
  // length taken from the previous setting's curve is not a rounding error —
  // it describes a different video.
  durationPending: 'Length updates once the new speed curve is calculated.',
  // Since #573 the analyzer seeds a curve for the default settings, so a newly
  // analyzed project lands here with a line already drawn. This is now the
  // narrow leftover case — a project analyzed before that change, or one whose
  // seed was skipped — and the copy says only what is actually true of it: a
  // save is the one thing that produces a curve. It used to offer "or reopen
  // this project in a moment", which was never true; nothing arrived on reopen.
  pending: 'The speed curve is calculated when this edit is saved. Move a control to see it.',
};

// The Fix flicker Strength tiles, KEYED BY THE WINDOW THEY SELECT in source
// frames. Only the words live here; the numbers are owned by
// lib/flickerPreview.js (DEFLICKER_WINDOW_CHOICES), which is the copy the
// cross-language drift test pins against the two Python modules. Keying by the
// frame count rather than pairing two ordered lists is deliberate: a
// list-and-list arrangement puts "Strong" against 5 frames the moment either
// order changes, and nothing on screen would look wrong.
//
// Each tile answers one question — what do I give up by choosing this — because
// the tradeoff is real in both directions. A short window leaves some strobing;
// a long one starts flattening light changes the shooter wanted (the whole
// "keeps real changes in light, like a sunset" promise). Neither is a "better"
// setting, so no tile is described as one.
//
// flickerPreview.test.js asserts this map and DEFLICKER_WINDOW_CHOICES cover
// exactly each other, so adding a window without copy (or copy without a
// window) fails the build rather than rendering a blank tile.
export const DEFLICKER_STRENGTHS = {
  5: {
    id: 'light',
    label: 'Light',
    tradeoff: 'Keeps fast light changes',
  },
  11: {
    id: 'standard',
    label: 'Standard',
    tradeoff: 'Best for most timelapses',
  },
  21: {
    id: 'strong',
    label: 'Strong',
    // Names the COST, not just the use case. The chart can only show what this
    // one WINS — the residual line drops and nothing on screen drops with it —
    // because the thing it gives up is a genuine fast light change being
    // flattened, and in a timelapse 21 frames is 21 capture intervals, so a
    // light coming on turns into a slow fade. Left as "For heavy strobing" the
    // three tiles read as a free upgrade and there is no reason to pick any
    // other one.
    tradeoff: 'For heavy strobing — softens fast light changes',
  },
};

// The watermark corner names, KEYED BY THE SETTINGS VALUE they label
// (`watermarkPosition` — wire format owned by lib/editDraft.js
// WATERMARK_POSITIONS). Shared by the watermark card's corner picker and the
// render dialog's summary line so the two never name the same corner
// differently. editDraft.test.js pins the two sets against each other, so a
// new corner without copy (or copy without a corner) fails the build.
export const WATERMARK_POSITION_LABELS = {
  'bottom-right': 'Bottom right',
  'bottom-left': 'Bottom left',
  'top-right': 'Top right',
  'top-left': 'Top left',
};

export default OPERATIONS;
